import { useEffect, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Alert, Button, DataTable, DetailList, Dialog, Field, Pagination } from "../../../ui";
import type { DataColumn } from "../../../ui";
import { HookFormField } from "../../../app/forms/HookFormField";
import type { Schemas } from "../../workspace/api.generated";
import type { Command } from "../../workspace/model";
import { failure } from "../../workspace/behavior";
import { businessCodeLabel, ceilWonAmount, date, documentTypeLabel, money } from "../../workspace/model";
import {
  CommandDialog,
  Kpi,
  PageHeader,
  Panel,
  RowAction,
  RowLink,
  StateNotice,
  statusBadge,
} from "../../workspace/shared";
import { GuidedActionButton } from "../../workspace/GuidedActionButton";
import { benefitSearchFormSchema } from "../schema";
import type { BenefitSearchFormValues } from "../schema";

type BenefitListState = "ready" | "loading" | "error";
type BenefitDetailState = "idle" | "ready" | "loading" | "error" | "denied" | "not-found";
type BenefitDialog = "request" | "approve" | "claim-reject" | "approval-reject" | "payout";

export interface BenefitDocumentPreview {
  readonly url: string;
  readonly contentType: string;
}

export interface BenefitViewProps {
  readonly mode?: "combined" | "list" | "detail";
  readonly filters: BenefitSearchFormValues;
  readonly result?: Schemas["AdminPageBenefitClaimView"];
  readonly listState?: BenefitListState;
  readonly detail?: Schemas["BenefitAdminDetailView"];
  readonly detailState?: BenefitDetailState;
  readonly selectedId?: number;
  readonly actorId: number;
  readonly canRequest: boolean;
  readonly canApprove: boolean;
  readonly unresolvedClaimId?: number;
  readonly refreshing?: boolean;
  readonly notice?: string;
  readonly recoveryError?: string;
  readonly onSearch: (filters: BenefitSearchFormValues) => void;
  readonly onPageChange: (page: number) => void;
  readonly onSelect: (claim: Schemas["BenefitClaimView"]) => void;
  readonly onRetryList: () => void;
  readonly onRetryDetail: () => void;
  readonly onRefresh: () => Promise<Schemas["BenefitAdminDetailView"] | undefined>;
  readonly onCommand: (command: Command) => Promise<unknown>;
  readonly onCommandComplete: (claim: Schemas["BenefitClaimView"]) => Promise<void>;
  readonly onUploadPayoutEvidence: (file: File) => Promise<Schemas["FileOperationResponse"]>;
  readonly onDownloadDocument: (
    document: Schemas["BenefitDocumentAdminView"],
  ) => Promise<BenefitDocumentPreview>;
  readonly onReleasePreview: (url: string) => void;
  readonly detailHref?: (claim: Schemas["BenefitClaimView"]) => string;
  readonly onOpenDetail?: (claim: Schemas["BenefitClaimView"]) => void;
  readonly backHref?: string;
  readonly backLabel?: string;
  readonly onBack?: () => void;
  readonly memberHref?: string;
  readonly onOpenMember?: () => void;
}

const listColumns: readonly DataColumn<Schemas["BenefitClaimView"]>[] = [
  { key: "number", title: "신청번호", render: (row) => row.claimNo },
  { key: "member", title: "회원 ID", numeric: true, render: (row) => row.memberId },
  { key: "type", title: "신청 유형", render: (row) => row.claimType.replaceAll("_", " ") },
  { key: "amount", title: "신청 금액", numeric: true, render: (row) => money(row.requestedAmount) },
  { key: "status", title: "상태", render: (row) => statusBadge(row.status) },
  { key: "submitted", title: "접수일시", render: (row) => date(row.submittedAt) },
];

const approvalColumns: readonly DataColumn<Schemas["BenefitApprovalAdminView"]>[] = [
  { key: "id", title: "기안 ID", numeric: true, render: (row) => row.approvalRequestId },
  { key: "requester", title: "기안자 ID", render: (row) => row.requestedByAdminLoginId ?? "—" },
  { key: "amount", title: "기안 금액", numeric: true, render: (row) => money(row.approvedAmount) },
  { key: "status", title: "상태", render: (row) => statusBadge(row.status) },
  { key: "reason", title: "기안 사유", render: (row) => row.reason },
  { key: "requested", title: "기안일시", render: (row) => date(row.requestedAt) },
  { key: "decider", title: "결정자 ID", render: (row) => row.decidedByAdminLoginId ?? "—" },
  { key: "decision", title: "결정 사유", render: (row) => row.decisionReason ?? "—" },
  { key: "decided", title: "결정일시", render: (row) => date(row.decidedAt) },
];

const reviewColumns: readonly DataColumn<Schemas["BenefitReviewAdminView"]>[] = [
  { key: "round", title: "회차", numeric: true, render: (row) => row.reviewRound },
  { key: "admin", title: "관리자 ID", render: (row) => row.adminLoginId ?? "—" },
  { key: "decision", title: "처리", render: (row) => statusBadge(row.decision) },
  { key: "amount", title: "승인 금액", numeric: true, render: (row) => money(row.approvedAmount) },
  { key: "reason", title: "사유", render: (row) => row.reason },
  { key: "reviewed", title: "처리일시", render: (row) => date(row.reviewedAt) },
];

function detailStatus(
  state: BenefitDetailState,
  onRetry: () => void,
) {
  if (state === "loading") return <p role="status">선택한 혜택 신청을 불러오는 중입니다.</p>;
  if (state === "denied") return <StateNotice state="denied" />;
  if (state === "not-found") return <StateNotice state="not-found" />;
  if (state === "error") {
    return (
      <Alert
        persistent
        variant="danger"
        title="혜택 신청 상세를 불러오지 못했습니다"
        action={<Button size="small" variant="quiet" onClick={onRetry}>다시 조회</Button>}
      >
        신청 상태와 처리 이력을 다시 조회해 주세요.
      </Alert>
    );
  }
  return null;
}

export function BenefitView({
  mode = "combined",
  filters,
  result,
  listState = "ready",
  detail,
  detailState = "idle",
  selectedId,
  actorId,
  canRequest,
  canApprove,
  unresolvedClaimId,
  refreshing = false,
  notice,
  recoveryError,
  onSearch,
  onPageChange,
  onSelect,
  onRetryList,
  onRetryDetail,
  onRefresh,
  onCommand,
  onCommandComplete,
  onUploadPayoutEvidence,
  onDownloadDocument,
  onReleasePreview,
  detailHref,
  onOpenDetail,
  backHref,
  backLabel,
  onBack,
}: BenefitViewProps) {
  const [dialog, setDialog] = useState<BenefitDialog | null>(null);
  const [uncertain, setUncertain] = useState(selectedId === unresolvedClaimId);
  const [refreshingDetail, setRefreshingDetail] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [upload, setUpload] = useState<Schemas["FileOperationResponse"]>();
  const [fileError, setFileError] = useState("");
  const [downloadingFileId, setDownloadingFileId] = useState<number>();
  const [selectedDocument, setSelectedDocument] = useState<Schemas["BenefitDocumentAdminView"]>();
  const [preview, setPreview] = useState<BenefitDocumentPreview>();
  const previewRef = useRef<BenefitDocumentPreview | undefined>(undefined);
  const form = useForm<BenefitSearchFormValues>({
    resolver: zodResolver(benefitSearchFormSchema),
    values: filters,
    shouldFocusError: true,
  });

  const releasePreview = () => {
    if (previewRef.current) onReleasePreview(previewRef.current.url);
    previewRef.current = undefined;
    setPreview(undefined);
  };

  const closePreview = () => {
    releasePreview();
    setSelectedDocument(undefined);
  };

  useEffect(() => {
    setDialog(null);
    setUpload(undefined);
    setFileError("");
    setUploading(false);
    if (previewRef.current) onReleasePreview(previewRef.current.url);
    previewRef.current = undefined;
    setPreview(undefined);
    setSelectedDocument(undefined);
  }, [onReleasePreview, selectedId]);

  useEffect(() => {
    setUncertain(selectedId === unresolvedClaimId);
  }, [selectedId, unresolvedClaimId]);

  useEffect(() => () => {
    if (previewRef.current) onReleasePreview(previewRef.current.url);
  }, [onReleasePreview]);

  const claim = detail?.claim;
  const pendingApproval = detail?.approvalRequests.find((approval) => (
    approval.status === "REQUESTED"
    && approval.approvalRequestId === claim?.approvalRequestId
  ));
  const selfRequested = pendingApproval?.requestedByAdminId === actorId;
  const requestAllowed = Boolean(claim?.status === "RECEIVED" && canRequest && !uncertain);
  const decisionAllowed = Boolean(
    claim?.status === "APPROVAL_PENDING"
    && pendingApproval
    && canApprove
    && !selfRequested
    && !uncertain,
  );
  const payoutAllowed = Boolean(claim?.status === "APPROVED" && canRequest && !uncertain);
  const requestBlockReason = !canRequest
    ? "승인 기안 또는 접수 반려 권한이 없습니다. 권한 관리자에게 요청해 주세요."
    : uncertain ? "처리 결과를 최신 상태 조회로 확인해 주세요."
      : "접수됨 상태에서만 기안 또는 접수 반려가 가능합니다. 현재 신청 상태를 확인해 주세요.";
  const decisionBlockReason = !canApprove
    ? "승인 결정 권한이 없습니다. 다른 승인 권한자에게 요청해 주세요."
    : uncertain ? "처리 결과를 최신 상태 조회로 확인해 주세요."
      : selfRequested ? "본인이 작성한 기안은 결정할 수 없습니다. 다른 승인 권한자가 처리해야 합니다."
        : !pendingApproval ? "결정할 승인 기안을 찾지 못했습니다. 최신 상태를 조회해 기안 ID를 확인해 주세요."
          : "승인 대기 상태에서만 기안 결정을 할 수 있습니다.";
  const payoutBlockReason = !canRequest
    ? "지급 기록 권한이 없습니다. 권한 관리자에게 요청해 주세요."
    : uncertain ? "처리 결과를 최신 상태 조회로 확인해 주세요."
      : "기안이 다른 관리자에게 승인된 뒤 지급 완료 상태를 기록할 수 있습니다.";

  const refreshLatest = async () => {
    if (!selectedId || refreshingDetail) return undefined;
    setRefreshingDetail(true);
    setFileError("");
    try {
      const latest = await onRefresh();
      setUncertain(false);
      return latest;
    } catch (cause) {
      setFileError(failure(cause).message);
      throw cause;
    } finally {
      setRefreshingDetail(false);
    }
  };

  const execute = async (command: Command) => {
    setUncertain(true);
    try {
      return await onCommand(command);
    } catch (cause) {
      if (!failure(cause).reconcile) setUncertain(false);
      throw cause;
    }
  };

  const complete = async (result: unknown) => {
    await onCommandComplete(result as Schemas["BenefitClaimView"]);
    setUncertain(false);
    setUpload(undefined);
    setFileError("");
  };

  const openDocument = async (document: Schemas["BenefitDocumentAdminView"]) => {
    if (!document.available || downloadingFileId !== undefined) return;
    setDownloadingFileId(document.fileAssetId);
    setFileError("");
    try {
      releasePreview();
      const downloaded = await onDownloadDocument(document);
      previewRef.current = downloaded;
      setPreview(downloaded);
      setSelectedDocument(document);
    } catch (cause) {
      setFileError(failure(cause).message);
    } finally {
      setDownloadingFileId(undefined);
    }
  };

  const uploadEvidence = async (file?: File) => {
    setUpload(undefined);
    setFileError("");
    if (!file) return;
    setUploading(true);
    try {
      setUpload(await onUploadPayoutEvidence(file));
    } catch (cause) {
      setFileError(failure(cause).message);
    } finally {
      setUploading(false);
    }
  };

  const documentColumns = [
    { key: "type", title: "증빙 유형", render: (row: Schemas["BenefitDocumentAdminView"]) => documentTypeLabel(row.documentTypeCode) },
    { key: "name", title: "파일명", render: (row: Schemas["BenefitDocumentAdminView"]) => row.originalFilename ?? "파일 정보 없음" },
    { key: "size", title: "크기", numeric: true, render: (row: Schemas["BenefitDocumentAdminView"]) => row.fileSize ? `${row.fileSize.toLocaleString("ko-KR")} bytes` : "—" },
    {
      key: "action",
      title: "작업",
      render: (row: Schemas["BenefitDocumentAdminView"]) => (
        <GuidedActionButton
          size="small"
          variant="quiet"
          blockedReason={!row.available ? "이 증빙 파일을 사용할 수 없습니다. 회원에게 파일을 다시 제출받거나 파일 처리 상태를 확인해 주세요." : null}
          isLoading={downloadingFileId === row.fileAssetId}
          onClick={() => { void openDocument(row); }}
        >
          {row.available ? "파일 보기" : "파일 없음"}
        </GuidedActionButton>
      ),
    },
  ] satisfies readonly DataColumn<Schemas["BenefitDocumentAdminView"]>[];

  const columns = [
    {
      key: "number",
      title: "신청번호",
      sticky: "start" as const,
      render: (row: Schemas["BenefitClaimView"]) => detailHref ? (
        <RowLink
          triggerKey={`benefit:${row.claimId}:detail`}
          href={detailHref(row)}
          onNavigate={onOpenDetail ? () => onOpenDetail(row) : undefined}
        >
          {row.claimNo}
        </RowLink>
      ) : row.claimNo,
    },
    ...listColumns.slice(1),
    ...(mode === "combined" ? [{
      key: "action",
      title: "작업",
      render: (row: Schemas["BenefitClaimView"]) => (
        <RowAction onClick={() => onSelect(row)}>신청 선택</RowAction>
      ),
    }] : []),
  ] satisfies readonly DataColumn<Schemas["BenefitClaimView"]>[];

  const activeDialogAmount = dialog === "request"
    ? claim?.requestedAmount
    : pendingApproval?.approvedAmount ?? claim?.approvedAmount ?? claim?.requestedAmount;
  const decisionTarget = pendingApproval
    ? `${claim?.claimNo} · 기안 ID ${pendingApproval.approvalRequestId}`
    : `${claim?.claimNo ?? "신청"} · 신청 ID ${claim?.claimId ?? selectedId ?? "—"}`;
  const dialogAllowed = dialog === "request" || dialog === "claim-reject"
    ? requestAllowed
    : dialog === "approve" || dialog === "approval-reject"
      ? decisionAllowed
      : payoutAllowed;
  const payoutFileReady = upload?.status === "READY" && Boolean(upload.fileAssetId);

  return (
    <>
      <PageHeader
        id={mode === "detail" ? "ADM-S24" : "ADM-S12"}
        title={mode === "detail" ? claim?.claimNo ?? "혜택지원 상세" : "혜택지원 심사"}
        description={mode === "detail" ? "신청 증빙과 승인·지급 이력을 확인하고 현재 가능한 업무를 처리합니다." : "신청 증빙을 확인하고 기안·독립 승인·수동 지급 기록을 순서대로 남깁니다."}
        breadcrumbs={mode === "detail" ? [{ label: "혜택지원 심사", href: "/benefits" }, { label: "혜택지원 상세", href: detailHref?.(claim ?? ({ claimId: selectedId } as Schemas["BenefitClaimView"])) ?? "#" }] : undefined}
        backAction={mode === "detail" && backHref && onBack ? { label: backLabel ?? "목록으로 돌아가기", href: backHref, onClick: onBack } : undefined}
        meta={mode !== "detail" && result && listState === "ready" ? <p aria-live="polite">총 {result.totalCount.toLocaleString("ko-KR")}건{refreshing ? " · 최신 목록 확인 중" : ""}</p> : undefined}
        action={mode !== "list" && selectedId ? <Button isLoading={refreshingDetail} size="small" variant="quiet" onClick={() => { void refreshLatest().catch(() => undefined); }}>최신 상태 조회</Button> : undefined}
      />
      {notice ? <Alert persistent variant="information" title="혜택지원 처리 결과">{notice}</Alert> : null}
      {unresolvedClaimId ? <StateNotice state="conflict" onRefresh={() => { void refreshLatest().catch(() => undefined); }} /> : null}
      {recoveryError ? <Alert persistent variant="warning" title="처리 결과 미확정">{recoveryError}</Alert> : null}
      {fileError ? <Alert persistent variant="danger" title="파일 또는 상태를 확인해 주세요">{fileError}</Alert> : null}
      {mode !== "detail" && result && listState === "ready" ? <div className="admin-kpi-grid"><Kpi label="검색 건수" value={`${result.totalCount.toLocaleString("ko-KR")}건`} /><Kpi label="현재 페이지 신청금액" value={money(result.items.reduce((sum, item) => sum + item.requestedAmount, 0))} note="전체 금액 집계는 API 확장 후 표시" /><Kpi label="심사 중" value={`${result.items.filter((item) => ["RECEIVED", "APPROVAL_PENDING"].includes(item.status)).length}건`} /><Kpi label="지급 완료" value={`${result.items.filter((item) => item.status === "PAID").length}건`} /></div> : null}
      {mode !== "detail" ? <><Panel title="검색 조건" description="회원 ID, 신청 상태와 접수 기간을 함께 적용할 수 있습니다.">
        <form
          className="admin-filter-bar"
          noValidate
          onSubmit={form.handleSubmit((values) => onSearch({ ...values, memberId: values.memberId.trim() }))}
        >
          <HookFormField compact control="select" formControl={form.control} label="신청 상태" name="status">
            <option value="">전체</option>
            <option value="RECEIVED">접수됨</option>
            <option value="APPROVAL_PENDING">승인 대기</option>
            <option value="APPROVED">승인 완료</option>
            <option value="PAID">지급 완료</option>
            <option value="REJECTED">반려</option>
          </HookFormField>
          <HookFormField compact formControl={form.control} inputMode="numeric" label="회원 ID" name="memberId" placeholder="숫자 ID" />
          <HookFormField compact formControl={form.control} label="접수 시작일" name="from" type="date" />
          <HookFormField compact formControl={form.control} label="접수 종료일" name="to" type="date" />
          <Button size="small" type="submit">조회</Button>
        </form>
      </Panel>
      <div>
        <DataTable
          caption="혜택 신청 검색 결과"
          columns={columns}
          rows={listState === "ready" ? result?.items ?? [] : []}
          rowKey={(row) => row.claimId}
          state={listState}
          onRetry={onRetryList}
        />
        {result && listState === "ready" ? (
          <Pagination page={result.page} size={result.size} totalCount={result.totalCount} onPageChange={onPageChange} />
        ) : null}
      </div>
      </> : null}
      {mode !== "list" && selectedId && detailState !== "ready" ? detailStatus(detailState, onRetryDetail) : null}
      {mode === "combined" && !selectedId && listState === "ready" ? (
        <Alert persistent variant="information" title="선택한 신청이 없습니다">
          {result?.items.length === 0 ? "조회된 혜택 신청이 없습니다. 검색 조건을 변경해 주세요." : "목록에서 처리할 혜택 신청을 선택해 주세요."}
        </Alert>
      ) : null}
      {mode !== "list" && claim && detail && detailState === "ready" ? (
        <>
          <div className="admin-split">
            <Panel title="신청 내용" description={claim.claimNo}>
              <DetailList items={[
                { label: "상태", value: statusBadge(claim.status) },
                { label: "접수일", value: date(claim.submittedAt) },
                { label: "회원명", value: detail.memberName ?? "—" },
                { label: "휴대폰번호", value: detail.phoneNumber ?? "—" },
                { label: "피해종류", value: businessCodeLabel(claim.claimType) },
                { label: "피해금액", value: money(claim.requestedAmount) },
                { label: "피해내용", value: claim.description },
                { label: "사고일", value: claim.incidentDate },
                { label: "지급일시", value: date(claim.paidAt) },
              ]} />
            </Panel>
            <Panel title="처리" description="접수 → 승인 기안 → 다른 관리자 승인 → 완료된 지급 기록">
              {selfRequested ? (
                <Alert persistent variant="warning" title="본인이 기안한 요청은 결정할 수 없습니다">
                  다른 승인 권한자가 승인 또는 기안 반려를 처리해야 합니다.
                </Alert>
              ) : null}
              {claim.status === "APPROVAL_PENDING" && !pendingApproval ? (
                <Alert persistent variant="warning" title="처리할 승인 기안을 찾지 못했습니다">
                  신청 상세를 다시 조회해 실제 승인 요청 ID를 확인해 주세요.
                </Alert>
              ) : null}
              {!canRequest ? <p>기안·접수 반려·지급 기록 권한이 없습니다.</p> : null}
              {!canApprove ? <p>승인 결정 권한이 없습니다.</p> : null}
              {uncertain ? <p>다시 처리하기 전에 최신 상태를 조회해 주세요.</p> : null}
              <div className="admin-detail-actions">
                <GuidedActionButton size="small" blockedReason={requestAllowed ? null : requestBlockReason} onClick={() => setDialog("request")}>승인 기안</GuidedActionButton>
                <GuidedActionButton size="small" variant="danger" blockedReason={requestAllowed ? null : requestBlockReason} onClick={() => setDialog("claim-reject")}>접수 반려</GuidedActionButton>
                <GuidedActionButton size="small" blockedReason={decisionAllowed ? null : decisionBlockReason} onClick={() => setDialog("approve")}>기안 승인</GuidedActionButton>
                <GuidedActionButton size="small" variant="danger" blockedReason={decisionAllowed ? null : decisionBlockReason} onClick={() => setDialog("approval-reject")}>기안 반려</GuidedActionButton>
                <GuidedActionButton size="small" variant="secondary" blockedReason={payoutAllowed ? null : payoutBlockReason} onClick={() => setDialog("payout")}>수동 지급 기록</GuidedActionButton>
              </div>
            </Panel>
          </div>
          <Panel title="제출 증빙" description={detail.documents.length === 0 ? "증빙 서류 없이 접수된 신청입니다." : "신청에 연결된 사용 가능한 파일만 열 수 있습니다."}>
            {detail.documents.length > 0 ? (
              <DataTable caption="혜택 신청 증빙" columns={documentColumns} rows={detail.documents} rowKey={(row) => row.fileAssetId} />
            ) : <p>첨부 없음</p>}
          </Panel>
          <Panel title="승인 기안 이력" description="기안자와 결정자는 서버에 기록된 관리자 ID로 확인합니다.">
            {detail.approvalRequests.length > 0 ? (
              <DataTable caption="혜택 승인 기안 이력" columns={approvalColumns} rows={detail.approvalRequests} rowKey={(row) => row.approvalRequestId} />
            ) : <p>아직 승인 기안이 없습니다.</p>}
          </Panel>
          <Panel title="심사 이력">
            {detail.reviews.length > 0 ? (
              <DataTable caption="혜택 심사 이력" columns={reviewColumns} rows={detail.reviews} rowKey={(row) => `${row.reviewRound}:${row.adminUserId}`} />
            ) : <p>아직 심사 이력이 없습니다.</p>}
          </Panel>
          <Panel title="지급 이력">
            {detail.payout ? (
              <DetailList items={[
                { label: "지급 방식", value: claim.payoutMethod ?? "MANUAL" },
                { label: "지급 참조번호", value: detail.payout.payoutReference ?? "—" },
                { label: "지급 증빙 파일 ID", value: detail.payout.evidenceFileId ?? "—" },
                { label: "기록 관리자 ID", value: detail.payout.paidByAdminLoginId ?? "—" },
                { label: "지급일시", value: date(detail.payout.paidAt) },
              ]} />
            ) : <p>기록된 수동 지급이 없습니다.</p>}
          </Panel>
          <Dialog
            open={Boolean(selectedDocument && preview)}
            onClose={closePreview}
            size="medium"
            title="혜택 증빙 미리보기"
            description={selectedDocument?.originalFilename ?? "증빙 파일"}
            primaryAction={<Button size="small" onClick={closePreview}>미리보기 닫기</Button>}
          >
            {selectedDocument && preview ? (
              <object data={preview.url} type={preview.contentType} width="100%" height="360" aria-label="혜택 증빙 파일">
                <a href={preview.url} download={selectedDocument.originalFilename ?? `혜택증빙-${selectedDocument.fileAssetId}`}>증빙 파일 저장</a>
              </object>
            ) : null}
          </Dialog>
          <CommandDialog
            key={`${claim.claimId}:${dialog ?? "closed"}`}
            open={dialog !== null}
            onClose={() => { setDialog(null); setUpload(undefined); setFileError(""); }}
            title={dialog === "request" ? "혜택 승인을 기안할까요?" : dialog === "payout" ? "완료된 수동 지급을 기록할까요?" : dialog === "approve" ? "혜택 기안을 승인할까요?" : "혜택 신청을 반려할까요?"}
            description={dialog === "payout" ? "실제 지급을 완료한 뒤 참조번호와 검사 완료 증빙을 기록합니다." : "실제 신청·기안 ID와 금액을 확인한 뒤 결정 사유를 남겨 주세요."}
            operation={dialog === "request" ? "혜택 승인 기안" : dialog === "payout" ? "수동 지급 결과 기록" : dialog === "approve" ? "혜택 기안 승인" : dialog === "approval-reject" ? "혜택 기안 반려" : "혜택 접수 반려"}
            target={dialog === "approve" || dialog === "approval-reject" ? decisionTarget : `${claim.claimNo} · 신청 ID ${claim.claimId}`}
            amount={money(activeDialogAmount)}
            confirmLabel={dialog === "request" ? "기안 저장" : dialog === "payout" ? "지급 기록" : dialog === "approve" ? "기안 승인" : "반려 저장"}
            danger={dialog === "claim-reject" || dialog === "approval-reject"}
            reasonLabel={dialog === "payout" ? "지급 참조번호" : "결정 사유"}
            reasonRequired
            reasonMaxLength={dialog === "payout" ? 200 : 2_000}
            confirmDisabled={!dialogAllowed || uploading || (dialog === "payout" && !payoutFileReady)}
            onCommand={execute}
            onRefresh={async () => { await refreshLatest(); }}
            onComplete={complete}
            buildCommand={(reason): Command => {
              if (dialog === "request") {
                return {
                  operation: "POST /api/v1/admin/benefits/{claimId}/approval-requests",
                  params: { claimId: claim.claimId },
                  body: { approvedAmount: ceilWonAmount(claim.requestedAmount), reason },
                };
              }
              if (dialog === "claim-reject") {
                return {
                  operation: "POST /api/v1/admin/benefits/{claimId}/reject",
                  params: { claimId: claim.claimId },
                  body: { reason },
                };
              }
              if (dialog === "approve" || dialog === "approval-reject") {
                return {
                  operation: "POST /api/v1/admin/benefits/approval-requests/{approvalId}/decision",
                  params: { approvalId: pendingApproval!.approvalRequestId, claimId: claim.claimId },
                  body: { approved: dialog === "approve", reason },
                };
              }
              return {
                operation: "POST /api/v1/admin/benefits/{claimId}/manual-payout",
                params: { claimId: claim.claimId },
                body: { payoutReference: reason, evidenceFileId: upload!.fileAssetId! },
              };
            }}
          >
            {dialog === "payout" ? (
              <>
                <Field
                  compact
                  type="file"
                  accept="application/pdf,image/jpeg,image/png"
                  label="지급 증빙 파일"
                  disabled={uploading}
                  onChange={(event) => { void uploadEvidence(event.currentTarget.files?.[0]); }}
                />
                {uploading ? <p role="status">지급 증빙을 업로드하고 검사 결과를 확인하는 중입니다.</p> : null}
                {upload ? (
                  <Alert
                    persistent
                    variant={upload.status === "READY" ? "information" : "warning"}
                    title={upload.status === "READY" ? "지급 증빙 준비 완료" : "지급 증빙 처리 중"}
                  >
                    {upload.originalFilename ?? "선택한 파일"} · {statusBadge(upload.status)}
                  </Alert>
                ) : null}
                {!uploading && !payoutFileReady ? <p>검사가 완료된 지급 증빙 파일을 선택해야 지급 기록을 저장할 수 있습니다.</p> : null}
              </>
            ) : null}
          </CommandDialog>
        </>
      ) : null}
    </>
  );
}
